/**
 * Sentry Error Monitoring Configuration
 * 
 * Initializes Sentry for error tracking, performance monitoring,
 * and session replay in the MEDD-SIM application.
 * 
 * DSN Setup:
 * 1. Go to https://sentry.io → Create account or sign in
 * 2. Create org: "Pacific Wave Digital"
 * 3. Create project: "medd-sim" (React platform)
 * 4. Copy the DSN and set it as VITE_SENTRY_DSN in:
 *    - .env (local development)
 *    - Vercel dashboard → Environment Variables (production)
 * 5. For source maps: Add SENTRY_AUTH_TOKEN to Vercel env vars
 */

import * as Sentry from '@sentry/react';
import { resolveSentryApp, resolveSentryEnvironment } from './sentryTarget';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
const DEPLOY_TARGET = __DEPLOY_TARGET__;
const SENTRY_RELEASE = __SENTRY_RELEASE__;
const SENTRY_APP = resolveSentryApp(DEPLOY_TARGET);

export function getEnvironment(): string {
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
  return resolveSentryEnvironment(DEPLOY_TARGET, hostname);
}

/**
 * Initialize Sentry SDK
 * Only initializes if a valid DSN is provided
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.info('[Sentry] No DSN configured — error monitoring disabled. Set VITE_SENTRY_DSN to enable.');
    return;
  }

  const environment = getEnvironment();

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    
    // Release tracking — set by Vite plugin or CI
    release: SENTRY_RELEASE || import.meta.env.VITE_SENTRY_RELEASE || undefined,

    // Performance Monitoring
    // Capture 100% in dev, 20% in production to stay within free tier
    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,

    // Session Replay — capture 10% of sessions, 100% of error sessions
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content and block all media for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out noise
    beforeSend(event) {
      // Don't send errors in development unless DSN is explicitly set
      if (environment === 'development' && !import.meta.env.VITE_SENTRY_DSN) {
        return null;
      }

      // Filter out known benign errors
      const message = event.exception?.values?.[0]?.value || '';
      
      // Browser extension interference
      if (message.includes('Extension context') || message.includes('chrome-extension://')) {
        return null;
      }

      // Network errors that are just connectivity issues
      if (message === 'Failed to fetch' || message === 'NetworkError when attempting to fetch resource.') {
        // Still send but tag as network error
        event.tags = { ...event.tags, error_type: 'network' };
      }

      return event;
    },

    // Only send errors from our domain
    allowUrls: [
      /sim\.medd\.com\.au/,
      /[a-z0-9-]+\.medd\.com\.au/,
      /medd-sim\.com/,
      /medd-sim.*\.vercel\.app/,
      /localhost/,
    ],

    // Ignore errors from third-party scripts
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  // Set default tags
  Sentry.setTag('app', SENTRY_APP);
  Sentry.setTag('platform', 'web');
  Sentry.setTag('deploy_target', DEPLOY_TARGET || 'local');

  console.info(`[Sentry] Initialized — environment: ${environment}`);
}

/**
 * Set user context when authenticated
 */
export function setSentryUser(user: { id: string; email?: string; name?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

// ============================================
// Custom Error Tracking for Key Flows
// ============================================

/**
 * Track simulation start/end failures
 */
export function captureSimulationError(
  phase: 'start' | 'end' | 'evaluation',
  error: unknown,
  context?: {
    simulationId?: string;
    agentId?: string;
    agentName?: string;
    platform?: string;
    userId?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', 'simulation');
    scope.setTag('simulation_phase', phase);
    if (context?.platform) scope.setTag('platform', context.platform);
    scope.setContext('simulation', {
      simulationId: context?.simulationId,
      agentId: context?.agentId,
      agentName: context?.agentName,
      platform: context?.platform,
      userId: context?.userId,
    });
    scope.setLevel('error');
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Track coaching session failures
 */
export function captureCoachingError(
  error: unknown,
  context?: {
    sessionId?: string;
    templateId?: string;
    userId?: string;
    phase?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', 'coaching');
    if (context?.phase) scope.setTag('coaching_phase', context.phase);
    scope.setContext('coaching', {
      sessionId: context?.sessionId,
      templateId: context?.templateId,
      userId: context?.userId,
    });
    scope.setLevel('error');
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Track PDF generation failures
 */
export function capturePdfError(
  error: unknown,
  context?: {
    evaluationId?: string;
    reportType?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', 'pdf_generation');
    if (context?.reportType) scope.setTag('report_type', context.reportType);
    scope.setContext('pdf', {
      evaluationId: context?.evaluationId,
      reportType: context?.reportType,
    });
    scope.setLevel('error');
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Track Supabase/API query failures
 */
export function captureApiError(
  error: unknown,
  context?: {
    operation?: string;
    table?: string;
    functionName?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', 'api');
    if (context?.operation) scope.setTag('api_operation', context.operation);
    if (context?.table) scope.setTag('api_table', context.table);
    scope.setContext('api', {
      operation: context?.operation,
      table: context?.table,
      functionName: context?.functionName,
    });
    scope.setLevel('error');
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Track Content Studio generation failures
 */
export function captureContentStudioError(
  error: unknown,
  context?: {
    action?: string;
    documentType?: string;
    generationType?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', 'content_studio');
    if (context?.action) scope.setTag('cs_action', context.action);
    if (context?.generationType) scope.setTag('cs_generation_type', context.generationType);
    scope.setContext('content_studio', {
      action: context?.action,
      documentType: context?.documentType,
      generationType: context?.generationType,
    });
    scope.setLevel('error');
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Track authentication failures
 */
export function captureAuthError(
  error: unknown,
  context?: {
    action?: string; // 'login' | 'signup' | 'password_reset' | 'session_refresh'
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', 'auth');
    if (context?.action) scope.setTag('auth_action', context.action);
    scope.setLevel('warning');
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Generic breadcrumb for tracking user actions
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
  });
}

// Re-export Sentry for direct access when needed
export { Sentry };
